<template>
  <!--
    Menu tab — full menu rendered as vertical category sections.
    Category pill bar is sticky and scroll-spies the page: scrolling the
    list updates the active pill via scroll listener; tapping a pill
    smooth-scrolls to that section.

    Data source: GET /v1/services/{token}/menu (signature-only, no auth
    token required). The API returns ALL products in one shot plus a
    `filter.categories` list — products may arrive in any order, so we
    group them by `product.category` (string label) here.
  -->
  <div class="menu-tab">

    <!-- Sticky search + category pill bar -->
    <div ref="stickyBar" class="sticky-bar bg-light">
      <!-- ค้นหาชื่ออาหาร — กรองสินค้าทุกหมวดพร้อมกัน หมวดที่ไม่เหลือรายการจะหายไปเอง
           (visibleCategories ตัดหมวดว่างอยู่แล้ว) จึงไม่ต้องมีโหมด "ผลการค้นหา" แยก -->
      <div class="px-3 pt-2 bg-light">
        <div class="menu-search">
          <i class="fa-solid fa-magnifying-glass menu-search__icon" />
          <input
            v-model="search"
            type="text"
            class="menu-search__input"
            :placeholder="$t('Search menu')"
            :aria-label="$t('Search menu')"
          >
          <button
            v-if="search"
            type="button"
            class="menu-search__clear"
            :aria-label="$t('Clear')"
            @click="search = ''"
          >
            <i class="fa-solid fa-xmark" />
          </button>
        </div>
      </div>

      <div ref="pillBar" class="pill-bar px-3 pt-2 pb-2 bg-light">
        <div v-if="loading" class="d-flex overflow-x-auto hide-scrollbar gap-2">
          <span
            v-for="i in 5"
            :key="'pill-ph-' + i"
            class="placeholder-glow flex-shrink-0"
          >
            <span class="placeholder rounded-pill" style="width: 78px; height: 30px;" />
          </span>
        </div>
        <div v-else class="d-flex overflow-x-auto hide-scrollbar gap-2">
          <button
            v-for="cat in visibleCategories"
            :key="cat.id"
            :ref="el => setPillRef(cat.id, el)"
            type="button"
            class="cat-pill flex-shrink-0"
            :class="{ active: activeCategoryId === cat.id }"
            @click="scrollToCategory(cat.id, $event)"
          >
            <i v-if="cat.id === RECOMMENDED_ID" class="fa-solid fa-star text-warning" />
            <span>{{ cat.id === RECOMMENDED_ID ? $t('Recommended') : $ln(cat) }}</span>
            <span class="cat-pill-count">{{ groupedProducts[cat.name]?.length || 0 }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Skeleton ↔ content swap uses a cross-fade so the menu eases in
         rather than snapping from grey boxes to product cards. -->
    <transition name="fade-swap" mode="out-in">
      <!-- Loading: full-page product skeleton -->
      <div v-if="loading" key="sk" class="px-3 pt-3 menu-skeleton-warm">
        <p-o-s-page-skeleton variant="product" :show-header="false" :card-count="8" />
      </div>

      <!-- Error: reachable retry -->
      <div v-else-if="error" key="err" class="px-3 pt-4 text-center text-muted">
        <i class="fa-solid fa-circle-exclamation fa-3x text-danger mb-3 opacity-75" />
        <p class="fw-bold mb-1">{{ $t('Failed to load menu') }}</p>
        <p class="small mb-3">{{ error }}</p>
        <button class="btn btn-theme text-white rounded-pill px-4" @click="fetchData">
          <i class="fa-solid fa-rotate me-2" />{{ $t('Try again') }}
        </button>
      </div>

    <!-- Category sections -->
    <!-- padding ล่าง = สูงแท็บ nav + สูงแถบตะกร้าลอย (วัดจริง) → การ์ดใบล่างไม่ถูกแถบทับ -->
    <div v-else key="real" class="px-3" :style="{ paddingBottom: bottomReserve + 'px' }">
      <template v-for="(cat, ci) in visibleCategories" :key="cat.id">
        <section
          :ref="el => setSectionRef(cat.id, el)"
          :data-cat-id="cat.id"
          class="menu-section mb-4 section-enter"
          :style="{ '--enter-delay': (ci * 25) + 'ms' }"
        >
          <div class="section-head d-flex align-items-center mb-2">
            <span class="section-accent" />
            <i v-if="cat.id === RECOMMENDED_ID" class="fa-solid fa-star me-2 text-warning" />
            <h6 class="section-title fw-bold text-dark mb-0">
              {{ cat.id === RECOMMENDED_ID ? $t('Recommended') : $ln(cat) }}
            </h6>
            <span class="section-count ms-2">{{ groupedProducts[cat.name].length }}</span>
          </div>

          <!-- เมนูแนะนำ = การ์ดโปสเตอร์ (รูปใหญ่ดึงสายตา) ชื่อ/รายละเอียดอยู่บนพื้นขาวใต้รูป
               หมวดอื่น = การ์ดแนวนอน สแกนชื่อ+รายละเอียด+ราคาได้เร็วกว่าตอนรายการยาว -->
          <div v-if="cat.id === RECOMMENDED_ID" class="row g-2">
            <div
              v-for="product in groupedProducts[cat.name]"
              :key="product.id"
              class="col-6 col-md-4 col-lg-3"
            >
              <div
                class="product-card shadow-sm h-100"
                :class="{ 'product-card--disabled': soldOut(product) }"
                @click="openProduct(product, $event)"
              >
                <div class="product-media">
                  <img
                    v-if="product.image_url || product.thumbnail_url"
                    :src="$storeIndex.menuImageUrl(product)"
                    class="product-image"
                    loading="lazy"
                    decoding="async"
                    @error="$storeIndex.onImageError"
                  >
                  <div v-else class="product-placeholder">
                    <i class="fa-solid fa-utensils" />
                  </div>

                  <!-- Top-left badge stack: price (with reco star) + options. -->
                  <div class="overlay-badges">
                    <span v-if="Number(product.price) > 0" class="overlay-price" :class="{ 'overlay-price--promo': product.promotion_price != null }">
                      <i v-if="product.is_recommend && !soldOut(product)" class="fa-solid fa-star text-warning me-1" />
                      <template v-if="product.promotion_price != null">
                        {{ $storeIndex.formatThaiCurrency(product.promotion_price) }}
                        <span class="text-decoration-line-through fw-normal ms-1" style="font-size: 0.72em; opacity: 0.7;">
                          {{ $storeIndex.formatThaiCurrency(product.price) }}
                        </span>
                      </template>
                      <template v-else>
                        {{ $storeIndex.formatThaiCurrency(product.price) }}
                        <span v-if="Number(product.compare_at_price) > Number(product.price)" class="text-decoration-line-through fw-normal ms-1" style="font-size: 0.72em; opacity: 0.7;">
                          {{ $storeIndex.formatThaiCurrency(product.compare_at_price) }}
                        </span>
                      </template>
                    </span>
                    <span
                      v-else-if="product.is_recommend && !soldOut(product)"
                      class="overlay-price overlay-price--reco"
                    >
                      <i class="fa-solid fa-star me-1" />{{ $t('Recommended') }}
                    </span>
                    <span v-if="product.has_variants || product.has_modifiers" class="overlay-options">
                      <i class="fa-solid fa-sliders me-1" />{{ $t('Options') }}
                    </span>
                  </div>

                  <span v-if="soldOut(product)" class="overlay-stock">{{ $t('Sold out') }}</span>
                </div>

                <!-- พื้นขาว: ชื่อ + รายละเอียดสินค้า + ปุ่มเพิ่ม (ปุ่มไม่มี handler
                     ของตัวเอง — คลิกจะ bubble ขึ้นไปที่ openProduct ของการ์ด) -->
                <div class="product-body">
                  <div class="product-body__text">
                    <h6 class="product-name mb-0">{{ $ln(product) }}</h6>
                    <p v-if="productDesc(product)" class="product-desc mb-0">{{ productDesc(product) }}</p>
                  </div>
                  <button
                    class="card-add-btn flex-shrink-0"
                    :class="{ 'card-add-btn--options': hasOptions(product) }"
                    :disabled="soldOut(product)"
                    :aria-label="hasOptions(product) ? $t('Options') : $t('Add to Cart')"
                  >
                    <i :class="hasOptions(product) ? 'fa-solid fa-sliders' : 'fa-solid fa-plus'" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="row g-2">
            <div
              v-for="product in groupedProducts[cat.name]"
              :key="product.id"
              class="col-12 col-md-6 col-xl-4"
            >
              <div
                class="product-row"
                :class="{ 'product-row--disabled': soldOut(product) }"
                @click="openProduct(product, $event)"
              >
                <div class="product-row__media">
                  <img
                    v-if="product.image_url || product.thumbnail_url"
                    :src="$storeIndex.menuImageUrl(product)"
                    class="product-row__image"
                    loading="lazy"
                    decoding="async"
                    @error="$storeIndex.onImageError"
                  >
                  <div v-else class="product-row__placeholder">
                    <i class="fa-solid fa-utensils" />
                  </div>
                  <span v-if="soldOut(product)" class="product-row__stock">{{ $t('Sold out') }}</span>
                </div>

                <div class="product-row__body">
                  <h6 class="product-name mb-0">
                    <i v-if="product.is_recommend && !soldOut(product)" class="fa-solid fa-star text-warning me-1" />{{ $ln(product) }}
                  </h6>
                  <p v-if="productDesc(product)" class="product-desc mb-0">{{ productDesc(product) }}</p>

                  <div class="product-row__foot">
                    <span v-if="Number(product.price) > 0" class="row-price" :class="{ 'row-price--promo': product.promotion_price != null }">
                      <template v-if="product.promotion_price != null">
                        {{ $storeIndex.formatThaiCurrency(product.promotion_price) }}
                        <span class="row-price__old text-decoration-line-through">
                          {{ $storeIndex.formatThaiCurrency(product.price) }}
                        </span>
                      </template>
                      <template v-else>
                        {{ $storeIndex.formatThaiCurrency(product.price) }}
                        <span v-if="Number(product.compare_at_price) > Number(product.price)" class="row-price__old text-decoration-line-through">
                          {{ $storeIndex.formatThaiCurrency(product.compare_at_price) }}
                        </span>
                      </template>
                    </span>
                    <button
                      class="card-add-btn flex-shrink-0"
                      :class="{ 'card-add-btn--options': hasOptions(product) }"
                      :disabled="soldOut(product)"
                      :aria-label="hasOptions(product) ? $t('Options') : $t('Add to Cart')"
                    >
                      <i :class="hasOptions(product) ? 'fa-solid fa-sliders' : 'fa-solid fa-plus'" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </template>

      <!-- ระหว่างค้นหาให้ทางออกในตัว (บอกให้ลองคำอื่น + ปุ่มล้างคำค้น) -->
      <div v-if="!loading && !error && visibleCategories.length === 0" class="text-center py-5 text-muted">
        <i class="fa-solid fa-bowl-rice fa-3x mb-3 opacity-50" />
        <p class="fw-bold mb-1">{{ $t('No products found') }}</p>
        <template v-if="search">
          <p class="small mb-3">{{ $t('Try a different search term') }}</p>
          <button type="button" class="btn btn-sm btn-theme text-white rounded-pill px-4" @click="search = ''">
            <i class="fa-solid fa-xmark me-1" />{{ $t('Clear') }}
          </button>
        </template>
      </div>
    </div>
    </transition>

    <!--
      Floating cart summary as a full-width bottom bar sitting just above
      the service tab nav. Mirrors the pattern used by Thai delivery apps
      (Grab / LINE MAN) so customers recognise it instantly. The hint line
      ("Tap to review…") lives inside the bar to spell out the second
      confirmation step — without it, customers often added items and
      didn't realise they had to open the cart and send.
    -->
    <div
      v-if="cartCount > 0"
      ref="floatingCartEl"
      class="floating-cart"
      :style="{ bottom: footerHeight + 'px' }"
      @click="showCart = true"
    >
      <!-- แถบตะกร้า = บล็อกเดียว (bg-theme, padding เดียว) — reward + แถวตะกร้าอยู่ด้วยกัน ไม่มีเส้นคั่น -->
      <div class="cart-bar bg-theme text-white px-3 py-2">
        <!-- reward — บรรทัดบนของบล็อกเดียวกัน (สีพื้นเดียวกับตะกร้า) -->
        <div
          v-if="rewardStatus && (rewardStatus.total > 0 || rewardStatus.nearestRemaining > 0)"
          class="cart-reward d-flex align-items-center gap-2 mb-2"
        >
          <span class="cart-reward__icon">
            <i :class="rewardStatus.total > 0 ? 'fa-solid fa-gift' : 'fa-solid fa-bullseye'" />
          </span>
          <div class="cart-reward__body">
            <!-- ถึงแล้ว → หัวข้อ "มีของแถมรอรับ" -->
            <div v-if="rewardStatus.total > 0" class="cart-reward__headline">
              {{ $t('You have rewards waiting') }}
            </div>
            <!-- nudge ขั้นถัดไป — โชว์ได้แม้มีของรอรับแล้ว (ซื้ออีก ฿X แลกได้อีก) -->
            <template v-if="rewardStatus.nearestRemaining > 0">
              <!-- ข้อความยาว → เลื่อนซ้ายช้าๆ เหมือนป้ายไฟ (เฉพาะตอน overflow) -->
              <div class="cart-reward__title" :class="{ 'cart-reward__title--sub': rewardStatus.total > 0 }" ref="rewardTitleBox">
                <span
                  ref="rewardTitleText"
                  class="cart-reward__title-text"
                  :class="{ 'is-marquee': rewardMarquee }"
                  :style="rewardMarquee ? { '--marquee-shift': rewardMarqueeShift + 'px', '--marquee-dur': rewardMarqueeDur + 's' } : null"
                >{{ nudgeText }}</span>
              </div>
              <div class="cart-reward__bar">
                <div class="cart-reward__bar-fill" :style="{ width: rewardStatus.nearestPercent + '%' }" />
              </div>
            </template>
          </div>
          <span v-if="rewardStatus.total > 0" class="cart-reward__count">{{ rewardStatus.total }}</span>
          <span v-else class="cart-reward__pct">{{ rewardStatus.nearestPercent }}%</span>
        </div>

        <!-- แถวตะกร้า -->
        <div class="cart-bar__row d-flex align-items-center">
          <i ref="cartIconEl" class="fa-solid fa-cart-shopping fa-lg me-3 cart-icon-bounce" />
          <div class="flex-grow-1 d-flex flex-column lh-sm">
            <span class="cart-confirm-text">
              <i class="fa-solid fa-circle-info me-1" />{{ $t('Tap to confirm your order') }}
            </span>
            <small class="cart-summary-text opacity-90">
              {{ cartCount }} {{ $t('items') }} <span class="mx-1 opacity-75">·</span> ฿{{ cartTotal }}
            </small>
          </div>
          <span class="fw-bold ms-2 text-nowrap">
            {{ $t('View Cart') }} <i class="fa-solid fa-chevron-right ms-1" />
          </span>
        </div>
      </div>
    </div>

    <!-- Cart offcanvas -->
    <div v-if="showCart" class="offcanvas-backdrop fade show" @click="showCart = false" />
    <div
      v-if="showCart"
      ref="cartSheet"
      class="position-fixed bottom-0 start-0 end-0 bg-white shadow-lg"
      style="z-index: 1050; max-height: 80vh; max-height: 80dvh; border-radius: 18px 18px 0 0; overflow-y: auto;"
    >
      <div class="p-3 border-bottom d-flex justify-content-between align-items-center sticky-top bg-white">
        <h5 class="mb-0 fw-bold">
          <i class="fa-solid fa-cart-shopping me-2 text-theme" />
          {{ $t('Your Cart') }}
        </h5>
        <button class="btn-close" @click="showCart = false" />
      </div>

      <!-- แจ้งบนสุดของตะกร้า: มีของแถมที่รับได้ → แตะเลื่อนไปแผงโปรเพื่อกดรับ -->

      <div class="p-3 pb-0">
        <button
          v-if="rewardStatus && rewardStatus.promoClaimable > 0"
          type="button"
          class="align-items-center d-flex gap-2 px-3 py-2 reward-cart-banner rounded-3 w-100"
          @click="scrollToPromo"
        >
          <span class="reward-cart-banner__ic"><i class="fa-solid fa-gift" /></span>
          <span class="flex-grow-1 text-start">
            <span class="reward-cart-banner__title d-block">{{ $t('You have {n} reward(s) to claim!', { n: rewardStatus.promoClaimable }) }}</span>
            <span class="reward-cart-banner__sub d-block">{{ $t('Tap to choose and claim below') }}</span>
          </span>
          <span class="reward-cart-banner__go"><i class="fa-solid fa-chevron-down" /></span>
        </button>

        <!-- ส่งไม่ผ่านเพราะของหมดระหว่างทาง — กู้สถานการณ์ให้จบในแตะเดียว
             (ไม่งั้นลูกค้าจะกดส่งวนเจอ error เดิมโดยไม่รู้ว่าต้องเอาอะไรออก) -->
        <div v-if="hasUnavailable" class="alert alert-danger d-flex align-items-center gap-2 mb-0 mt-2 py-2 px-3 rounded-3">
          <i class="fa-solid fa-triangle-exclamation" />
          <div class="flex-grow-1 small text-start">{{ $t('_cart.Sorry - some items cannot be ordered right now') }}</div>
          <button
            type="button"
            class="btn btn-sm btn-danger flex-shrink-0 text-nowrap"
            :disabled="sendingOrder"
            @click="$emit('remove-unavailable')"
          >
            {{ $t('_cart.Remove and continue') }}
          </button>
        </div>
      </div>

      <div class="p-3">
        <div v-if="cart.length === 0" class="text-center text-muted py-5">
          <i class="fa-solid fa-cart-shopping fa-2x mb-2 opacity-50" />
          <p class="mb-0">{{ $t('No new items') }}</p>
        </div>

        <div
          v-for="item in cart"
          :key="item._uid"
          class="cart-item card border-0 shadow-sm rounded-4 mb-2"
          :class="{ 'cart-item--unavailable': isUnavailable(item) }"
        >
          <div class="card-body p-3">
            <!-- Title + line total -->
            <div class="d-flex justify-content-between align-items-start mb-1">
              <div class="flex-grow-1 pe-2" style="min-width: 0;">
                <div class="fw-bold text-dark text-break">{{ item.variant_name? $ln(item, 'variant_name'): $ln(item) }}</div>
              </div>
              <span class="fw-bold text-theme">฿{{ item.unit_price * item.quantity }}</span>
            </div>

            <!-- สั่งไม่ได้ระหว่างที่ถือตะกร้าอยู่ — ฝั่งลูกค้าบอกแค่ 2 แบบ: หมด กับ งดจำหน่าย
                 ไม่บอกสาเหตุ/ชื่อวัตถุดิบ/สถานะสาขา: ลูกค้าไม่ได้ประโยชน์ แถมเป็นข้อมูลหลังบ้าน
                 (รายละเอียดเต็มอยู่ฝั่ง POS ซึ่งพนักงานต้องรู้ว่าต้องไปแก้อะไร) -->
            <div v-if="isUnavailable(item)" class="mb-1">
              <span class="badge bg-danger-subtle text-danger border border-danger-subtle fw-normal">
                <i class="fa-solid fa-circle-exclamation me-1" />{{ customerLabel(item._unavailable, $t.bind(this)) }}
              </span>
            </div>

            <!-- Modifier groups: subtle subtitle lines, no chip noise -->
            <div v-if="item.selected_modifiers && item.selected_modifiers.length" class="mb-1">
              <div
                v-for="g in item.selected_modifiers"
                :key="g.id"
                class="small lh-sm"
              >
                <span class="text-muted">{{ $ln(g) }}:</span>
                <span class="text-dark">{{ g.options.map(o => $ln(o)).join(', ') }}</span>
              </div>
            </div>

            <div v-if="item.remark" class="mt-1">
              <span class="badge bg-light text-dark border small fw-normal">
                <i class="fa-solid fa-comment-dots me-1 text-theme" />{{ item.remark }}
              </span>
            </div>

            <!-- Pill stepper (theme-tinted) + icon-only edit/trash on the right -->
            <div class="d-flex align-items-center justify-content-between mt-3">
              <div class="qty-pill d-inline-flex align-items-center">
                <button
                  type="button"
                  class="qty-btn"
                  :aria-label="$t('Decrease')"
                  @click="decrement(item)"
                >
                  <i class="fa-solid fa-minus" />
                </button>
                <span class="qty-value">{{ item.quantity }}</span>
                <button
                  type="button"
                  class="qty-btn"
                  :aria-label="$t('Increase')"
                  @click="increment(item)"
                >
                  <i class="fa-solid fa-plus" />
                </button>
              </div>
              <div class="d-flex align-items-center gap-1">
                <button
                  type="button"
                  class="btn btn-sm text-theme border-0 px-2"
                  :title="$t('Edit')"
                  @click="reopenItem(item)"
                >
                  <i class="fa-solid fa-pen-to-square" />
                </button>
                <button
                  type="button"
                  class="btn btn-sm text-danger border-0 px-2"
                  :title="$t('Remove')"
                  @click="$emit('remove-cart-item', item._uid)"
                >
                  <i class="fa-solid fa-trash-can" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div ref="promoPanelEl" class="px-3 pt-3">
        <service-promotion-panel :cart-total="cartTotal" :initial-data="bundledPromotion" :auto-fetch="false" @claims-changed="$emit('promo-claims-changed', $event)" />
      </div>

      <div class="bg-white p-3 border-top sticky-bottom">
        <div class="d-flex justify-content-between mb-2">
          <span class="text-muted">{{ $t('Total') }}</span>
          <span class="fs-5 fw-bold text-theme">฿{{ cartTotal }}</span>
        </div>
        <button
          class="btn btn-theme text-white w-100 py-2 fw-bold rounded-3"
          :disabled="cart.length === 0 || sendingOrder"
          @click="sendOrder"
        >
          <i :class="sendingOrder ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-paper-plane'" class="me-2" />
          {{ sendingOrder ? $t('Confirming') + '...' : $t('Confirm Order') }}
        </button>
      </div>
    </div>

    <!-- Product customisation sheet -->
    <product-options-sheet
      v-if="optionsProduct"
      :product="optionsProduct"
      :edit-item="editItem"
      @close="closeOptions"
      @add="onAddFromSheet"
      @update="onUpdateFromSheet"
    />
  </div>
</template>

<script>
import { ServiceMenu, isServiceSessionInvalid } from '@/services/endpoint/services.service';
import POSPageSkeleton from '@/plugins/POSPageSkeleton.vue';
import ProductOptionsSheet from './ProductOptionsSheet.vue';
import ServicePromotionPanel from './ServicePromotionPanel.vue';
import { useScrollRestore } from '@/composables/useScrollRestore';
import { customerLabel } from '@/utils/unavailableItems';
import { pickLocalized } from '@/utils/i18nName';
import { stripHtml } from '@/utils/helpers';

const RECOMMENDED_ID = '__recommended';
const RECOMMENDED_NAME = '__recommended_section__';

export default {
  name: 'TabMenu',
  components: { ProductOptionsSheet, POSPageSkeleton, ServicePromotionPanel },
  props: {
    cart: { type: Array, default: () => [] },
    // สรุปสถานะ reward จาก container (ส่งมาเฉพาะแท็บเมนู) — ใช้โชว์แถบบอกในตะกร้า
    rewardStatus: { type: Object, default: null },
    // Parent flips this to true while POST /orders is in flight so the
    // Send button can show a spinner + disable to prevent double-fire.
    sendingOrder: { type: Boolean, default: false },
    // เพิ่มขึ้น 1 ทุกครั้งที่ส่งไม่ผ่านเพราะของหมด → trigger เลื่อนไปแถวที่ไฮไลต์
    // (counter ไม่ใช่ boolean เพราะส่งซ้ำแล้วพังชุดเดิม ค่า boolean จะไม่เปลี่ยน → watcher ไม่ยิง)
    unavailableSeq: { type: Number, default: 0 },
  },
  emits: ['add-to-cart', 'update-cart-item', 'remove-cart-item', 'remove-unavailable', 'clear-cart', 'send-order', 'promo-claims-changed', 'rewards', 'ready', 'session-invalid'],
  data() {
    return {
      RECOMMENDED_ID,
      // --- API state -----------------------------------------------------
      loading: true,            // first-load + retry: shows full skeleton
      error: '',                // user-visible error message (empty = no error)
      categories: [],           // [{ id, name }] from filter.categories
      products: [],             // raw products from /menu (one shot, no pagination)
      bundledPromotion: null,   // promotion ที่มากับ response เมนู → ส่งให้ panel ในตัว (ไม่ fetch แยก)

      // --- UI state ------------------------------------------------------
      search: '',               // คำค้นชื่ออาหาร (กรองฝั่ง FE — เมนูมาครบทีเดียวอยู่แล้ว)
      activeCategoryId: null,
      sectionEls: {},      // refs keyed by category id
      pillEls: {},
      optionsProduct: null,
      editItem: null,
      showCart: false,
      footerHeight: 0,     // measured nav height so the floating cart sits just above it
      cartBarHeight: 0,    // measured floating-cart height (สูงขึ้นเมื่อมี reward ribbon) — เผื่อ padding ล่างเมนู
      _suppressSpyUntil: 0, // ms-timestamp; scroll-spy ignored until then (during click-scroll)
      _onScroll: null,
      _onResize: null,
      _rafPending: false,  // throttle scroll-driven recompute to once per frame
      // reward ribbon marquee (ข้อความยาว → เลื่อนเหมือนป้ายไฟ)
      rewardMarquee: false,
      rewardMarqueeShift: 0,
      rewardMarqueeDur: 6,
    };
  },
  computed: {
    // แถวไหนถูก parent ติดธงว่าสั่งไม่ได้ → ขับแบนเนอร์ "นำออกแล้วส่ง" เหนือรายการในตะกร้า
    // อ่านจาก cart ตรง ๆ ไม่ต้องรับเป็น prop แยก (แหล่งความจริงเดียว)
    hasUnavailable() {
      return this.cart.some(i => i._unavailable && i._unavailable.length);
    },
    // ข้อความ nudge ขั้นถัดไป "ซื้ออีก ฿X รับ Y" (โชว์ได้ทั้งตอนยังไม่ถึง + ตอนมีของรอรับแล้ว)
    nudgeText() {
      const r = this.rewardStatus;
      if (!r || r.nearestRemaining <= 0) return '';
      if (r.nearestType === 'quantity') {
        return this.$t('Buy {n} more to get {name}', { n: r.nearestRemaining + ' ' + this.$t('_promotion.pcs'), name: this.$t(r.nearestName) });
      }
      return this.$t('Spend {amount} more to get {name}', { amount: this.$storeIndex.formatThaiCurrency(r.nearestRemaining), name: this.$t(r.nearestName) });
    },
    /**
     * ค้นหาชื่ออาหาร: กรองที่ต้นทางแล้วปล่อยให้ grouping/scroll-spy ทำงานต่อตามปกติ
     * (หมวดที่ไม่เหลือรายการถูกตัดใน visibleCategories อยู่แล้ว) — ค้นทั้งชื่อไทยและ
     * อังกฤษเสมอ ไม่ผูกกับภาษาที่เลือก เพราะลูกค้าพิมพ์สลับภาษากันบ่อย
     */
    filteredProducts() {
      const keyword = this.search.trim().toLowerCase();
      if (!keyword) return this.products;
      return this.products.filter(p =>
        (p.name || '').toLowerCase().includes(keyword)
        || (p.name_en || '').toLowerCase().includes(keyword)
      );
    },
    recommendedProducts() {
      return this.filteredProducts.filter(p => p.is_recommend);
    },
    /**
     * Group products by category NAME (the menu list returns a string
     * label for `category` — no id back-ref). Display order follows the
     * `filter.categories` array from the API, so even if products arrive
     * unsorted they always render category-grouped in the right order.
     * Recommended items get a virtual bucket at the top.
     */
    groupedProducts() {
      const groups = {};
      if (this.recommendedProducts.length) {
        groups[RECOMMENDED_NAME] = this.recommendedProducts;
      }
      for (const cat of this.categories) groups[cat.name] = [];
      for (const p of this.filteredProducts) {
        if (groups[p.category]) {
          groups[p.category].push(p);
        } else if (p.category) {
          // Defensive: if the product's category isn't in filter.categories,
          // create a bucket so the item still shows up.
          groups[p.category] = [p];
        }
      }
      return groups;
    },
    /** Hide empty categories so the pill bar isn't padded with no-op tabs. */
    visibleCategories() {
      // Include categories returned by the API plus any "extra" ones we
      // discovered while bucketising rogue products.
      const known = new Set(this.categories.map(c => c.name));
      const extras = Object.keys(this.groupedProducts)
        .filter(name => name !== RECOMMENDED_NAME && !known.has(name))
        .map(name => ({ id: 'x-' + name, name }));
      const real = [...this.categories, ...extras]
        .filter(c => (this.groupedProducts[c.name] || []).length > 0);
      const list = [];
      if (this.recommendedProducts.length) {
        list.push({ id: RECOMMENDED_ID, name: RECOMMENDED_NAME });
      }
      list.push(...real);
      return list;
    },
    cartCount() {
      return this.cart.reduce((s, i) => s + i.quantity, 0);
    },
    cartTotal() {
      return this.cart.reduce((s, i) => s + i.unit_price * i.quantity, 0);
    },
    // เผื่อพื้นที่ล่างเมนูเฉพาะ "แถบตะกร้าลอย" (แท็บ nav มี padding-bottom ของ <main> เผื่อให้แล้ว
    // → ไม่ต้องบวก footerHeight ซ้ำ ไม่งั้นสูงเกิน). ตะกร้าว่าง = 0 → ไม่เผื่อเกินจำเป็น
    bottomReserve() {
      return this.cartBarHeight > 0 ? this.cartBarHeight + 12 : 0;
    },
  },
  watch: {
    // เนื้อหา ribbon เปลี่ยน (ข้อความ nudge หรือ headline/total) → วัด marquee + ความสูงแถบใหม่
    nudgeText: {
      handler() { this.$nextTick(() => { this.measureRewardMarquee(); this.measureCartBar(); }); },
      immediate: true,
    },
    'rewardStatus.total'() {
      this.$nextTick(() => { this.measureRewardMarquee(); this.measureCartBar(); });
    },
    // ตะกร้ามี/ไม่มีของ → แถบลอยโผล่/หาย → วัดความสูงใหม่
    cartCount() {
      this.$nextTick(() => this.measureCartBar());
    },
    visibleCategories: {
      // If the set changes (e.g. menu reloads), keep an active pill that
      // still exists or fall back to the first one.
      handler(next) {
        if (!next.length) {
          this.activeCategoryId = null;
          return;
        }
        if (!next.find(c => c.id === this.activeCategoryId)) {
          this.activeCategoryId = next[0].id;
        }
        this.$nextTick(() => this.updateActiveFromScroll());
      },
    },
    activeCategoryId(id) {
      this.scrollActivePillIntoView(id);
    },
    // ผลลัพธ์เปลี่ยนชุด → ดีดกลับหัวหน้า ไม่งั้นจะค้างอยู่ตำแหน่ง scroll เดิมของผลชุดก่อน
    // (ซึ่งมักเลยรายการที่เหลืออยู่ไปแล้ว ทำให้ดูเหมือนไม่เจออะไรเลย)
    search() {
      this.$nextTick(() => {
        if (window.scrollY > 0) window.scrollTo({ top: 0 });
        this.updateActiveFromScroll();
      });
    },
    // parent ติดธงหลังส่งไม่ผ่าน → พาสายตาไปที่แถวที่สั่งไม่ได้ (ฝั่งลูกค้าไม่มี toast แล้ว)
    unavailableSeq(n) {
      if (n > 0) this.revealUnavailable();
    },
  },
  mounted() {
    this.measureFooter();

    this._onScroll = () => {
      // rAF throttle: collapse a burst of scroll events into one update.
      if (this._rafPending) return;
      this._rafPending = true;
      requestAnimationFrame(() => {
        this._rafPending = false;
        this.updateActiveFromScroll();
      });
    };
    this._onResize = () => {
      this.measureFooter();
      this.updateActiveFromScroll();
    };
    window.addEventListener('scroll', this._onScroll, { passive: true });
    window.addEventListener('resize', this._onResize);

    this.fetchData();

    useScrollRestore(() => this.$refs.cartSheet);
  },
  beforeUnmount() {
    if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
    if (this._onResize) window.removeEventListener('resize', this._onResize);
  },
  methods: {
    // ธง `_unavailable` ติดโดย container หลังโดน 422 ERROR_ITEM_UNAVAILABLE
    isUnavailable(item) {
      return !!(item._unavailable && item._unavailable.length);
    },
    // expose util ให้เทมเพลตเรียกได้ (ยุบ reason เหลือ 2 กลุ่มด้วยภาษาบริการ)
    customerLabel,
    // สินค้าสั่งไม่ได้ตอนนี้ (ของหมด/วัตถุดิบไม่พอ) — ต่างจาก isUnavailable ที่ดูรายการในตะกร้า
    soldOut(product) {
      return !product.is_in_stock || product.is_ingredient_insufficient;
    },
    // กดแล้วเปิดแผ่นตัวเลือกแทนที่จะหย่อนลงตะกร้าเลย — ปุ่มบนการ์ดใช้ค่านี้
    // เลือกไอคอน (sliders vs +) ให้ตรงกับสิ่งที่จะเกิดขึ้นจริง
    hasOptions(product) {
      return !!(product.has_variants || product.has_modifiers);
    },
    /**
     * รายละเอียดสินค้าสำหรับโชว์ใต้ชื่อเมนูบนการ์ด
     *
     * API ส่งเป็น HTML (sanitize แล้ว) และแผ่นรายละเอียดก็ render ตรง ๆ แต่บนการ์ด
     * ยุบเป็นข้อความล้วนก่อน เพราะช่องนี้คือ teaser 2 บรรทัดที่คุมด้วย -webkit-line-clamp
     * ซึ่งใช้กับลูกที่เป็น block (<p>/<ul>) ไม่ได้ — จะกลายเป็นการ์ดสูงไม่เท่ากันทั้งแถว
     *
     * ใช้ pickLocalized ตรง ๆ (ไม่ผ่าน $ln) เพราะ $ln ส่งค่าเข้า $t อีกชั้น ซึ่งเหมาะกับ
     * "ชื่อ" ที่อาจมีคีย์ในพจนานุกรม แต่ประโยคบรรยายไม่มีทางมีคีย์อยู่แล้ว
     */
    productDesc(product) {
      return stripHtml(pickLocalized(product, this.$i18n.locale, 'description'));
    },
    // banner ในตะกร้า → เลื่อนลงไปแผงโปรเพื่อกดรับ
    scrollToPromo() {
      this.$refs.promoPanelEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    },
    // วัดว่า ribbon text ยาวเกินกล่องไหม → เปิด marquee + คำนวณระยะ/ความเร็ว (px คงที่ ~26px/s)
    measureRewardMarquee() {
      const box = this.$refs.rewardTitleBox;
      const text = this.$refs.rewardTitleText;
      if (!box || !text) { this.rewardMarquee = false; return; }
      const overflow = text.scrollWidth - box.clientWidth;
      if (overflow > 4) {
        this.rewardMarquee = true;
        this.rewardMarqueeShift = Math.ceil(overflow + 8); // เผื่อขอบนิดหน่อย
        this.rewardMarqueeDur = Math.max(3, Math.round((this.rewardMarqueeShift / 26) * 10) / 10);
      } else {
        this.rewardMarquee = false;
      }
    },
    // --- Ref helpers (Vue 3 function refs) -------------------------------
    setSectionRef(id, el) {
      if (el) this.sectionEls[id] = el;
      else delete this.sectionEls[id];
    },
    setPillRef(id, el) {
      if (el) this.pillEls[id] = el;
      else delete this.pillEls[id];
    },

    // --- Scroll-spy ------------------------------------------------------
    /**
     * Pick the active category by reading each section's current top
     * relative to the viewport. The active one is the LAST section whose
     * top has scrolled past our trigger line (= just under the sticky
     * search + pill bar, plus a small visual buffer).
     *
     * This replaces an IntersectionObserver approach that proved fragile
     * when section heights varied and during smooth-scroll: between two
     * sections the band briefly intersected nothing and the active pill
     * would stick to the previous one.
     */
    updateActiveFromScroll() {
      if (Date.now() < this._suppressSpyUntil) return;
      if (!this.visibleCategories.length) return;

      const trigger = this.stickyOffset() + 8; // 8px buffer so the flip happens just before the header text touches the bar
      let activeId = this.visibleCategories[0].id;

      for (const cat of this.visibleCategories) {
        const el = this.sectionEls[cat.id];
        if (!el) continue;
        const top = el.getBoundingClientRect().top;
        if (top <= trigger) {
          activeId = cat.id;
        } else {
          // visibleCategories is in display order — once we hit a section
          // still below the trigger, every subsequent one is too.
          break;
        }
      }

      if (activeId !== this.activeCategoryId) {
        this.activeCategoryId = activeId;
      }
    },

    measureFooter() {
      const nav = document.querySelector('.service-shell > nav');
      this.footerHeight = nav ? nav.offsetHeight : 0;
      this.measureCartBar();
    },
    // ความสูงแถบตะกร้าลอย (รวม reward ribbon) — ใช้กันการ์ดใบล่างถูกทับ
    measureCartBar() {
      this.cartBarHeight = this.$refs.floatingCartEl ? this.$refs.floatingCartEl.offsetHeight : 0;
    },

    /**
     * Combined height of header + sticky bar — used as both the
     * smooth-scroll offset and the scroll-spy trigger line. Reading
     * the sticky-bar's bottom in viewport coords gives us the exact
     * point where it ends once stuck, so we don't need to track the
     * header height separately (CSS handles that via --header-h).
     */
    stickyOffset() {
      const stickyBar = this.$refs.stickyBar;
      return stickyBar ? stickyBar.getBoundingClientRect().bottom : 0;
    },

    scrollToCategory(id, ev) {
      const el = this.sectionEls[id];
      if (!el) return;
      // On touch devices the tapped button keeps `:focus` and a sticky
      // `:hover` state which makes Bootstrap's outline-secondary fill in
      // gray — making non-active pills look active. Releasing focus here
      // clears both states immediately.
      ev?.currentTarget?.blur();

      // Optimistically set active so the pill highlight responds instantly.
      this.activeCategoryId = id;
      // Suppress scroll-spy during the smooth scroll so intermediate
      // sections passing the trigger line don't flicker the active pill.
      this._suppressSpyUntil = Date.now() + 700;

      const top = el.getBoundingClientRect().top + window.scrollY - this.stickyOffset() - 4;
      window.scrollTo({ top, behavior: 'smooth' });
    },

    scrollActivePillIntoView(id) {
      const pill = this.pillEls[id];
      const bar = this.$refs.pillBar;
      if (!pill || !bar) return;
      const scroller = bar.querySelector('.hide-scrollbar');
      if (!scroller) return;
      const pillRect = pill.getBoundingClientRect();
      const barRect = scroller.getBoundingClientRect();
      // Centre the active pill horizontally inside the visible pill bar.
      const target = scroller.scrollLeft
        + (pillRect.left - barRect.left)
        - (barRect.width / 2)
        + (pillRect.width / 2);
      scroller.scrollTo({ left: target, behavior: 'smooth' });
    },

    // --- API ------------------------------------------------------------
    async fetchData() {
      const token = this.$route?.params?.token;
      if (!token) {
        this.error = this.$t('Missing service session token');
        this.loading = false;
        return;
      }
      this.loading = true;
      this.error = '';
      let sessionInvalid = false;
      try {
        const res = await ServiceMenu.list(token);
        const payload = res.data?.data ?? [];
        const filter = res.data?.filter ?? {};
        this.products = Array.isArray(payload) ? payload : [];
        this.categories = Array.isArray(filter.categories) ? filter.categories : [];

        // promotion + loyalty มากับ response เมนู (bundled — ยิงเส้นเดียว):
        //  - bundledPromotion → ส่งให้ ServicePromotionPanel ในตัว (ไม่ fetch เอง)
        //  - emit rewards → parent อัปเดต badge แท็บบิล + CTA (แทน refreshRewards แยก)
        this.bundledPromotion = res.data?.promotion ?? null;
        this.$emit('rewards', { promotion: this.bundledPromotion, loyalty: res.data?.loyalty ?? null });

        // Seed active pill from the first non-empty category once data lands.
        const firstVisible = this.visibleCategories[0];
        this.activeCategoryId = firstVisible ? firstVisible.id : null;
      } catch (err) {
        // Dead session / bogus token → let the shell redirect to the public
        // preview. Skip the inline error + ready emit so the boot overlay
        // stays up through the navigation (no broken-session flash).
        if (isServiceSessionInvalid(err)) {
          sessionInvalid = true;
          this.$emit('session-invalid');
          return;
        }
        const msg = err?.response?.data?.message || err?.message || this.$t('Network error');
        this.error = msg;
        this.products = [];
        this.categories = [];
      } finally {
        this.loading = false;
        if (!sessionInvalid) this.$emit('ready');
        // Recompute scroll-spy now that real sections exist.
        this.$nextTick(() => this.updateActiveFromScroll());
      }
    },

    // --- Product interactions -------------------------------------------
    async openProduct(product, event) {
      if (!product.is_in_stock || product.is_ingredient_insufficient) return;
      // The menu-list response tells us up-front whether a product needs the
      // customisation sheet. For simple items we skip the detail API entirely
      // and add straight to cart at the listed price.
      const sourceRect = event?.currentTarget?.getBoundingClientRect();
      const needsSheet = product.has_variants || product.has_modifiers;
      if (!needsSheet) {
        this.$emit('add-to-cart', {
          product_id: product.id,
          variant_id: null,
          name: product.name,
          name_en: product.name_en,
          variant_name: null,
          variant_name_en: null,
          unit_price: parseFloat(product.price) || 0,
          quantity: 1,
          remark: '',
          modifier_option_ids: [],
          selected_modifiers: [],
        });
        // Wait for the parent to push the new item so the floating cart
        // (v-if="cartCount > 0") is mounted before we measure its position.
        await this.$nextTick();
        this.flyToCart(sourceRect, product.image_url);
        return;
      }
      this._pendingSourceRect = sourceRect;
      this._pendingImageUrl = product.image_url;
      this.editItem = null;
      this.optionsProduct = product;
    },
    reopenItem(cartItem) {
      const product = this.products.find(p => p.id === cartItem.product_id);
      if (!product) return;
      this.editItem = cartItem;
      this.optionsProduct = product;
    },
    closeOptions() {
      this.optionsProduct = null;
      this.editItem = null;
    },
    async onAddFromSheet(cartItem) {
      const sourceRect = this._pendingSourceRect;
      const imageUrl = this._pendingImageUrl;
      this.$emit('add-to-cart', cartItem);
      this.closeOptions();
      await this.$nextTick();
      this.flyToCart(sourceRect, imageUrl);
    },
    onUpdateFromSheet(cartItem) {
      this.$emit('update-cart-item', {
        uid: this.editItem._uid,
        patch: cartItem,
      });
      this.closeOptions();
    },
    increment(item) {
      this.$emit('update-cart-item', {
        uid: item._uid,
        patch: { quantity: item.quantity + 1 },
      });
    },
    decrement(item) {
      if (item.quantity <= 1) {
        this.$emit('remove-cart-item', item._uid);
        return;
      }
      this.$emit('update-cart-item', {
        uid: item._uid,
        patch: { quantity: item.quantity - 1 },
      });
    },
    /**
     * ห้ามหุบตะกร้าตรงนี้ — ต้องรอผลจาก API ก่อน
     * สำเร็จ → parent สลับไปแท็บ "ออเดอร์ของฉัน" ซึ่ง unmount TabMenu ทั้งตัว (drawer หายเอง)
     * ไม่สำเร็จ → drawer ยังเปิดค้าง ลูกค้าเห็นแถวที่ไฮไลต์ + ปุ่มนำออกได้ทันที
     * (เดิมหุบทิ้งทันทีตั้งแต่ยังไม่ยิง ลูกค้าเลยไม่มีทางเห็นว่าเมนูไหนสั่งไม่ได้)
     */
    sendOrder() {
      this.$emit('send-order');
    },
    // ส่งไม่ผ่าน → เปิดตะกร้า (เผื่อลูกค้าเพิ่งกดปิดระหว่างรอ) แล้วเลื่อนไปแถวแรกที่สั่งไม่ได้
    // ปุ่มส่งอยู่ล่างสุด แต่แบนเนอร์อยู่บนสุด — ถ้าไม่เลื่อนให้ ลูกค้าจะไม่เห็นอะไรเปลี่ยนเลย
    revealUnavailable() {
      this.showCart = true;
      this.$nextTick(() => {
        const el = this.$refs.cartSheet?.querySelector('.cart-item--unavailable');
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    },

    /**
     * Fly an image chip from the tapped product card down to the floating
     * cart along a curved bezier so customers actually *see* the item
     * travel into the basket (Grab/foodpanda-style affordance). All
     * elements are appended to <body> as transient overlays — no Vue
     * reactivity touched.
     */
    flyToCart(sourceRect, imageUrl) {
      if (!sourceRect) return;
      const cartEl = this.$refs.floatingCartEl;
      if (!cartEl) return;
      // Respect users who've opted out of motion at the OS level.
      if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
      // offset-path is the cleanest way to drive an element along a curve;
      // bail gracefully if the browser is too old rather than animating linearly.
      if (!CSS.supports?.('offset-path', "path('M0 0 L1 1')")) return;

      const cartRect = cartEl.getBoundingClientRect();
      const startX = sourceRect.left + sourceRect.width / 2;
      const startY = sourceRect.top + sourceRect.height / 2;
      const endX = cartRect.left + cartRect.width / 2;
      const endY = cartRect.top + cartRect.height * 0.4;

      // Control point lifted above the higher of the two endpoints — this
      // gives the chip a "tossed" arc rather than a straight diagonal,
      // which reads more like the "หยิบใส่ตะกร้า" gesture.
      const midX = (startX + endX) / 2;
      const midY = Math.min(startY, endY) - Math.max(80, Math.abs(endX - startX) * 0.35);
      const d = `M ${startX} ${startY} Q ${midX} ${midY} ${endX} ${endY}`;

      // --- Flying chip (mini card following the path) --------------------
      const flyer = document.createElement('div');
      Object.assign(flyer.style, {
        position: 'fixed', left: '0', top: '0', width: '64px', height: '64px',
        borderRadius: '14px', overflow: 'hidden', pointerEvents: 'none',
        zIndex: '9999', boxShadow: '0 8px 20px rgba(0,0,0,0.28)',
        background: '#fff',
        offsetPath: `path('${d}')`,
        offsetRotate: '0deg',
        offsetAnchor: 'center',
      });
      if (imageUrl) {
        flyer.style.backgroundImage = `url('${imageUrl}')`;
        flyer.style.backgroundSize = 'cover';
        flyer.style.backgroundPosition = 'center';
      } else {
        Object.assign(flyer.style, {
          background: '#e96c15', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '22px',
        });
        flyer.innerHTML = '<i class="fa-solid fa-utensils"></i>';
      }
      document.body.appendChild(flyer);

      const flight = flyer.animate(
        [
          { offsetDistance: '0%',   transform: 'scale(1)',    opacity: 1 },
          { offsetDistance: '70%',  transform: 'scale(0.65)', opacity: 0.95, offset: 0.7 },
          { offsetDistance: '100%', transform: 'scale(0.25)', opacity: 0.3 },
        ],
        { duration: 780, easing: 'cubic-bezier(0.55, 0.05, 0.7, 0.4)', fill: 'forwards' },
      );

      flight.onfinish = () => {
        flyer.remove();
        // A short one-shot bump on the cart icon — WAAPI temporarily
        // overrides the infinite CSS bounce, then yields back to it.
        const icon = this.$refs.cartIconEl;
        if (icon) {
          icon.animate(
            [
              { transform: 'scale(1)' },
              { transform: 'scale(1.45) translateY(-5px)' },
              { transform: 'scale(0.92)' },
              { transform: 'scale(1)' },
            ],
            { duration: 420, easing: 'ease-out' },
          );
        }
      };
    },
  },
};
</script>

<style scoped>
/*
  Warm, brand-tinted shimmer for the customer menu skeleton only.
  POSPageSkeleton is shared with the staff/back-office POS, so we override
  its shimmer here (scoped + :deep) instead of recolouring the component —
  the POS keeps its neutral grey placeholder. Matches the warm recipe in
  the sibling service tabs (TabOrders / TabBill / TabService).
*/
.menu-skeleton-warm :deep(.pos-skeleton__shimmer) {
  background: linear-gradient(100deg,
    #f4e6d8 0%,
    #fcefe4 42%,
    #fff9f3 50%,
    #fcefe4 58%,
    #f4e6d8 100%);
  background-size: 200% 100%;
  animation-duration: 1.4s;
}

/*
  The bar sticks just below the customer header. `--header-h` is declared
  on `.service-shell` and matches the header's `min-height`, so CSS alone
  keeps the two flush across reloads — no JS measurement needed.
  z-index sits above the menu cards but below modals/offcanvas (1050+).
*/
.sticky-bar {
  position: sticky;
  top: var(--header-h, 4.75rem);
  z-index: 2;
  /* Match the header's collapse animation so the pill bar rides up with
     it (--header-h shrinks on scroll) instead of snapping. */
  transition: top 0.25s ease;
}

.hide-scrollbar::-webkit-scrollbar { display: none; }
.hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }

/* --- ช่องค้นหาชื่ออาหาร ------------------------------------------------
   ทรงเดียวกับ pill ด้านล่าง (rounded-pill + เส้นบาง) ให้แถบ sticky ดูเป็นชุดเดียว */
.menu-search { position: relative; }
.menu-search__icon {
  position: absolute;
  left: 0.85rem;
  top: 50%;
  transform: translateY(-50%);
  color: #adb5bd;
  font-size: 0.85rem;
  pointer-events: none;
}
.menu-search__input {
  width: 100%;
  padding: 0.45rem 2.3rem;
  font-size: 0.9rem;
  line-height: 1.4;
  color: #333;
  background: #fff;
  border: 1px solid #e7e7e7;
  border-radius: 999px;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
}
.menu-search__input::placeholder { color: #adb5bd; }
.menu-search__input:focus {
  outline: none;
  border-color: #e96c15;
  box-shadow: 0 0 0 3px rgba(233, 108, 21, 0.15);
}
.menu-search__clear {
  position: absolute;
  right: 0.4rem;
  top: 50%;
  transform: translateY(-50%);
  width: 26px;
  height: 26px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.06);
  color: #6c757d;
  font-size: 0.75rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.menu-search__clear:hover { background: rgba(0, 0, 0, 0.12); }

/* --- Poster product card (เมนูแนะนำ) ---------------------------------
   รูปเต็มกรอบด้านบน + แถบพื้นขาวใต้รูปสำหรับชื่อ/รายละเอียด/ปุ่มเพิ่ม
   (เดิมชื่อวางทับรูปบน gradient — อ่านยากเมื่อรูปสว่างและไม่มีที่ให้คำบรรยาย)
   ป้ายราคา/ตัวเลือก/ของหมด ยังลอยอยู่บนรูปเหมือนเดิม */
.product-card {
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  background: #fff;
  border: 1px solid #f0f0f0;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}
.product-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 14px 26px rgba(0, 0, 0, 0.16) !important;
}
.product-card--disabled { opacity: 0.55; }
.product-media {
  position: relative;
  aspect-ratio: 1 / 1;            /* square poster */
  overflow: hidden;
  background: #1d1d1d;            /* fallback shows through if image fails */
}
.product-image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}
.product-card:hover .product-image { transform: scale(1.06); }
.product-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #e96c15 0%, #ff8a3c 100%);
  color: rgba(255, 255, 255, 0.7);
  font-size: 2.5rem;
}
/* แถบพื้นขาว: ข้อความชิดซ้าย ปุ่มเพิ่มชิดขวาล่าง */
.product-body {
  flex-grow: 1;
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
  padding: 0.5rem 0.6rem 0.6rem;
  background: #fff;
}
.product-body__text {
  flex-grow: 1;
  min-width: 0;
}
.product-name {
  font-size: 0.85rem;
  font-weight: 700;
  color: #212529;
  /* 1.8 (ไม่ใช่ 1.25) กันวรรณยุกต์บน (ก๋, เล้ง) และสระล่าง (ตุ๋น) ถูก
     กล่อง line-clamp (overflow:hidden) ตัดหัว/ตัดหาง */
  line-height: 1.8;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
}
.product-desc {
  margin-top: 0.1rem;
  font-size: 0.72rem;
  line-height: 1.65;
  color: #8a8f98;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
}

/* --- การ์ดแนวนอน (หมวดทั่วไป) ---------------------------------------
   รูปเล็กซ้าย + ชื่อ/รายละเอียด/ราคา ขวา — บนจอมือถือลูกค้ากวาดสายตาหาเมนู
   ได้เร็วกว่าตารางโปสเตอร์ และมีที่ว่างพอให้คำบรรยายเมนูจริง ๆ */
.product-row {
  height: 100%;
  display: flex;
  gap: 0.7rem;
  padding: 0.55rem;
  cursor: pointer;
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}
.product-row:hover {
  transform: translateY(-2px);
  border-color: rgba(233, 108, 21, 0.35);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}
.product-row--disabled { opacity: 0.6; }
.product-row__media {
  position: relative;
  flex-shrink: 0;
  width: 88px;
  height: 88px;
  border-radius: 11px;
  overflow: hidden;
  background: #1d1d1d;
}
.product-row__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.product-row__placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #e96c15 0%, #ff8a3c 100%);
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.5rem;
}
/* ของหมด: แถบคาดล่างรูป (ไม่ใช่ป้ายกลาง) — รูปเล็ก ป้ายกลางจะบังทั้งใบ */
.product-row__stock {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 0.12rem 0;
  text-align: center;
  background: rgba(0, 0, 0, 0.78);
  color: #fff;
  font-size: 0.62rem;
  font-weight: 700;
}
.product-row__body {
  flex-grow: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.product-row__foot {
  margin-top: auto;          /* ราคา/ปุ่ม ยึดขอบล่างเสมอ ไม่ว่าคำบรรยายจะกี่บรรทัด */
  padding-top: 0.35rem;
  display: flex;
  /* บนแท็บเล็ต (lg + cart panel เปิด) การ์ดเหลือ ~230px ที่ว่างข้างรูปจึงสั้นมาก
     wrap + margin-left:auto ทำให้ราคาที่ยาว (ราคาโปรฯ + ราคาเดิม) ดันปุ่มตก
     บรรทัดใหม่แทนที่จะล้นออกนอกกรอบการ์ด */
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 0.35rem 0.5rem;
}
.product-row__foot .card-add-btn { margin-left: auto; }
.row-price {
  display: inline-flex;
  flex-wrap: wrap;           /* ราคาโปรฯ + ราคาเดิม ตกบรรทัดกันได้ */
  align-items: baseline;
  column-gap: 0.3rem;
  font-size: 0.92rem;
  font-weight: 800;
  color: #e96c15;
  white-space: nowrap;
}
.row-price--promo { color: #16a34a; }
.row-price__old {
  font-size: 0.72rem;
  font-weight: 500;
  color: #adb5bd;
}

.overlay-badges {
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.3rem;
  z-index: 2;
  max-width: calc(100% - 1rem);
}
.overlay-price {
  display: inline-flex;
  align-items: center;
  background: #e96c15;
  color: #fff;
  font-weight: 800;
  padding: 0.22rem 0.6rem;
  border-radius: 999px;
  font-size: 0.76rem;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.25);
  white-space: nowrap;
  max-width: 100%;
}
.overlay-price--reco { background: #ffc107; color: #1d1d1d; }
.overlay-price--promo { background: #16a34a; }
.overlay-options {
  display: inline-flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.95);
  color: #d46010;
  border: 1px solid rgba(233, 108, 21, 0.25);
  font-weight: 600;
  padding: 0.18rem 0.5rem;
  border-radius: 999px;
  font-size: 0.66rem;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(4px);
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
}
/* Centred over the (dimmed) image so it never collides with the price /
   options chips in the top-left corner, no matter how wide the price is. */
.overlay-stock {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 3;
  background: rgba(0, 0, 0, 0.82);
  color: #fff;
  padding: 0.3rem 0.85rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.35);
}
.card-add-btn {
  width: 30px;
  height: 30px;
  padding: 0;
  border: none;
  border-radius: 999px;
  background: #e96c15;
  color: #fff;
  font-size: 0.8rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.3);
  transition: transform 0.12s ease, background 0.15s ease;
}
.card-add-btn:hover:not(:disabled) { background: #d46010; }
.card-add-btn:active:not(:disabled) { transform: scale(0.9); }
/* มีตัวเลือก (variant/modifier) = กดแล้วเปิดแผ่นตัวเลือก ไม่ใช่หย่อนลงตะกร้าเลย
   จึงใช้ปุ่มโปร่งไอคอน sliders แยกจากปุ่ม + ทึบที่เพิ่มของเข้าตะกร้าทันที
   (แทนชิป "ตัวเลือก" เดิมที่กินที่จนล้นกรอบการ์ดบนแท็บเล็ต) */
.card-add-btn--options {
  background: #fff;
  color: #e96c15;
  border: 1.5px solid rgba(233, 108, 21, 0.55);
  box-shadow: 0 2px 6px rgba(233, 108, 21, 0.18);
}
.card-add-btn--options:hover:not(:disabled) { background: #fff3e9; }
/* ปุ่มอยู่บนพื้นขาวแล้ว (ไม่ใช่ทับรูป) — disabled จึงเป็นเทาให้เห็นว่ากดไม่ได้ */
.card-add-btn--options:disabled { border-color: #dee2e6; color: #adb5bd; }
.card-add-btn:disabled {
  background: #dee2e6;
  box-shadow: none;
  cursor: not-allowed;
}
@media (max-width: 380px) {
  .product-name { font-size: 0.78rem; }
  .product-desc { font-size: 0.68rem; }
  .overlay-price { font-size: 0.68rem; padding: 0.2rem 0.5rem; }
  .overlay-options { font-size: 0.6rem; }
  .product-row__media { width: 76px; height: 76px; }
}

/*
  Compact category pills. Lighter and smaller than the old Bootstrap
  outline buttons — a thin chip that fills with the theme colour when
  active. Using a custom button (not `.btn-outline-secondary`) also
  sidesteps the sticky touch `:hover`/`:focus` fill Bootstrap applied,
  which used to leave a tapped pill looking active.
*/
.cat-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.7rem;
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 1.4;
  white-space: nowrap;
  color: #6c757d;
  background: #fff;
  border: 1px solid #e7e7e7;
  border-radius: 999px;
  cursor: pointer;
  transition: color 0.18s ease, background-color 0.18s ease,
              border-color 0.18s ease, box-shadow 0.18s ease;
}
.cat-pill:focus,
.cat-pill:focus-visible {
  outline: none;
  box-shadow: none;
}
.cat-pill.active {
  color: #fff;
  background: #e96c15;
  border-color: #e96c15;
  box-shadow: 0 3px 10px rgba(233, 108, 21, 0.3);
}
.cat-pill-count {
  font-size: 0.68rem;
  font-weight: 700;
  min-width: 1.15rem;
  padding: 0 0.3rem;
  text-align: center;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.06);
  color: inherit;
}
.cat-pill.active .cat-pill-count {
  background: rgba(255, 255, 255, 0.25);
}

/*
  Slimmer section header. A short theme-coloured accent bar + a small
  heading + a muted count, replacing the old `h5` + "X items" badge that
  ate too much vertical space and read heavier than the cards below it.
*/
.section-head {
  margin-top: 0.25rem;
}
.section-accent {
  width: 3px;
  height: 1rem;
  border-radius: 999px;
  background: #e96c15;
  margin-right: 0.55rem;
}
.section-title {
  font-size: 1rem;
  letter-spacing: 0.1px;
}
.section-count {
  font-size: 0.72rem;
  font-weight: 700;
  color: #adb5bd;
}

/*
  Bottom offset is bound inline from `footerHeight` (measured nav height
  including safe-area padding), so the bar sits flush above the tab nav
  on every device without hardcoding heights.
*/
.floating-cart {
  position: fixed;
  left: 0;
  right: 0;
  z-index: 5;
  cursor: pointer;
  /* รวม ribbon + cart row เป็นแถบเดียว: โค้งมุมบน + clip ลูก + เงาขึ้นบน */
  border-radius: 16px 16px 0 0;
  overflow: hidden;
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.16);
}
/* reward — บรรทัดบนของบล็อกตะกร้าเดียวกัน (สีพื้นเดียว ไม่มี padding/เส้นคั่นแยก)
   เว้นด้านล่างด้วย hairline จางๆ ให้ดูเป็นระเบียบโดยไม่กลายเป็นแถบแยก */
.cart-reward {
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.16);
}
.cart-reward__icon {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 0.85rem;
  background: rgba(255, 255, 255, 0.25);
  color: #fff;
}
.cart-reward__body { flex-grow: 1; min-width: 0; }
/* หัวข้อ "มีของแถมรอรับ" (ตอนถึงแล้ว) */
.cart-reward__headline {
  font-weight: 800;
  font-size: 0.86rem;
  line-height: 1.25;
}
/* กล่องครอบ — ซ่อนส่วนล้น ให้ข้อความเลื่อนข้างในแบบป้ายไฟ */
.cart-reward__title {
  overflow: hidden;
}
/* nudge รอง (อยู่ใต้ headline ตอนมีของรอรับแล้ว) — เล็ก/จางลงนิด */
.cart-reward__title--sub { margin-top: 1px; }
.cart-reward__title--sub .cart-reward__title-text {
  font-size: 0.74rem;
  font-weight: 600;
  opacity: 0.92;
}
.cart-reward__title-text {
  display: inline-block;
  white-space: nowrap;
  font-weight: 700;
  font-size: 0.82rem;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: bottom;
}
/* ยาวเกิน → เลื่อนซ้ายช้าๆ แล้วกลับ (ping-pong) เหมือนป้ายไฟ */
.cart-reward__title-text.is-marquee {
  max-width: none;
  text-overflow: clip;
  animation: cart-reward-marquee var(--marquee-dur, 6s) ease-in-out infinite alternate;
}
@keyframes cart-reward-marquee {
  0%, 12% { transform: translateX(0); }
  88%, 100% { transform: translateX(calc(-1 * var(--marquee-shift, 0px))); }
}
.cart-reward__bar {
  margin-top: 4px;
  height: 5px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.28);
  overflow: hidden;
}
.cart-reward__bar-fill {
  height: 100%;
  border-radius: 999px;
  background: #fff;
  transition: width 0.4s ease;
}
.cart-reward__count {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 7px;
  border-radius: 999px;
  background: #fff;
  color: var(--bs-theme, #ea8a04);
  font-size: 0.82rem;
  font-weight: 800;
}
.cart-reward__pct {
  flex-shrink: 0;
  font-size: 0.82rem;
  font-weight: 800;
  color: #fff;
  font-variant-numeric: tabular-nums;
}
@media (prefers-reduced-motion: reduce) {
  .cart-reward__title-text.is-marquee { animation: none; }
}
/* banner ในตะกร้า — CTA เขียวเด่น กดได้ เลื่อนไปแผงโปร (โทนเดียวกับปุ่ม "รับเลย") */
.reward-cart-banner {
  position: relative;
  overflow: hidden;
  width: calc(100% - 1.5rem);
  border: none;
  border-radius: 14px !important;
  background: linear-gradient(135deg, #1aa251, #15803d);
  color: #fff;
  font-weight: 600;
  transition: transform 0.12s ease, box-shadow 0.12s ease, filter 0.12s ease;
  box-shadow: 0 6px 16px rgba(21, 128, 61, 0.32);
}
.reward-cart-banner:active { transform: scale(0.985); filter: brightness(0.97); }
/* แสงวิ่งผ่านบางๆ ให้ดูพรีเมียม + ดึงสายตา */
.reward-cart-banner::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(110deg, transparent 35%, rgba(255, 255, 255, 0.22) 50%, transparent 65%);
  background-size: 220% 100%;
  animation: reward-banner-sheen 3s ease-in-out infinite;
  pointer-events: none;
}
.reward-cart-banner__ic {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.22);
  color: #fff;
  font-size: 1rem;
}
.reward-cart-banner__title { font-weight: 800; font-size: 0.9rem; line-height: 1.25; }
.reward-cart-banner__sub { font-size: 0.72rem; font-weight: 500; opacity: 0.9; }
.reward-cart-banner__go {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.95);
  color: #15803d;
  font-size: 0.95rem;
  animation: reward-banner-bob 1.4s ease-in-out infinite;
}
@keyframes reward-banner-bob {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(3px); }
}
@keyframes reward-banner-sheen {
  0% { background-position: 180% 0; }
  60%, 100% { background-position: -80% 0; }
}
@media (prefers-reduced-motion: reduce) {
  .reward-cart-banner__go { animation: none; }
  .reward-cart-banner::after { animation: none; opacity: 0; }
}
.cart-bar {
  border-radius: 0; /* มุมโค้งย้ายไปที่ .floating-cart (clip รวม ribbon) */
  animation: cart-pulse 1.8s ease-in-out infinite;
}
.cart-confirm-text {
  font-size: 1rem;
  font-weight: 700;
}
.cart-summary-text {
  font-size: 0.78rem;
}

/*
  Subtle pulsing glow + icon bounce to draw customer attention to the
  floating cart so they remember the second confirmation step. Keep it
  gentle — strong flashing on a fixed bar quickly becomes annoying.
  Respect prefers-reduced-motion for accessibility.
*/
@keyframes cart-pulse {
  0%, 100% {
    box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.15),
                0 0 0 0 rgba(233, 108, 21, 0.55);
  }
  50% {
    box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.2),
                0 0 0 8px rgba(233, 108, 21, 0);
  }
}
.cart-icon-bounce {
  animation: cart-icon-bounce 1.8s ease-in-out infinite;
  display: inline-block;
}
@keyframes cart-icon-bounce {
  0%, 100% { transform: translateY(0) scale(1); }
  50%      { transform: translateY(-2px) scale(1.12); }
}
@media (prefers-reduced-motion: reduce) {
  .cart-bar,
  .cart-icon-bounce {
    animation: none;
  }
}

/*
  แถวที่ server ตีกลับว่าสั่งไม่ได้แล้ว — ขอบแดง + พื้นแดงจาง ให้สแกนเจอทันที
  ว่าต้องเอาแถวไหนออก (ยังกดแก้/ลบได้ตามปกติ จึงไม่หรี่ opacity ทั้งใบ)
*/
.cart-item--unavailable {
  border: 1px solid var(--bs-danger-border-subtle, #f1aeb5) !important;
  background: var(--bs-danger-bg-subtle, #f8d7da33);
}

/*
  Quantity stepper styled as a single rounded pill in a light theme tint.
  The buttons are borderless circles inside the pill — feels lighter and
  more modern than the boxy btn-group it replaced.
*/
.qty-pill {
  background: rgba(233, 108, 21, 0.08); /* theme-color @ 8% */
  border: 1px solid rgba(233, 108, 21, 0.25);
  border-radius: 999px;
  padding: 2px;
}
.qty-btn {
  width: 30px;
  height: 30px;
  border: none;
  background: transparent;
  color: #d46010;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  transition: background-color 0.15s;
}
.qty-btn:hover {
  background-color: rgba(233, 108, 21, 0.15);
}
.qty-btn:active {
  background-color: rgba(233, 108, 21, 0.25);
}
.qty-value {
  min-width: 28px;
  text-align: center;
  font-weight: 700;
  color: #333;
  font-size: 0.9rem;
}

/*
  scroll-margin-top tells the browser how much room to leave above a section
  when something inside it is focused/scrolled-into-view (e.g. anchor jump),
  matching our manual offset so keyboard navigation lands cleanly too.
*/
.menu-section {
  scroll-margin-top: 140px;
}

/* Short cross-fade — keeps the skeleton → menu handover responsive. */
.fade-swap-enter-active,
.fade-swap-leave-active {
  transition: opacity 0.12s ease;
}
.fade-swap-enter-from,
.fade-swap-leave-to {
  opacity: 0;
}

/*
  Per-section entry stagger. Tight (~25ms apart, 0.25s per section) so
  the menu feels like it's "settling into place" without making the
  user wait through a slow ripple after the skeleton clears.
*/
.section-enter {
  animation: section-enter-in 0.25s ease-out both;
  animation-delay: var(--enter-delay, 0ms);
}
@keyframes section-enter-in {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}
</style>
